<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseAdvancedFilter from '@/components/common/Filters/BaseAdvancedFilter.vue'
import BasePage from '@/components/Layouts/BasePage.vue'
import BaseQuickSearch from '@/components/common/Filters/BaseQuickSearch.vue'
import BaseTable from '@/components/tables/BaseTable.vue'
import ButtonNew from '@/components/common/button/ButtonNew.vue'
import DialogConfirmDelet from '@/components/common/DialogConfirmDelet.vue'
import RHDigitalLogoCell from '@/components/rhdigital/list/RHDigitalLogoCell.vue'
import { computeClass } from '@/utils/styleFormats.js'
import { getRHDigitalMockActions } from '@/services/api/rhdigital.js'

const props = defineProps({
    entity: {
        type: Object,
        required: true,
    },
    fetcher: {
        type: Function,
        required: true,
    },
})

const { t } = useI18n()
const items = ref([])
const totalItems = ref(0)
const loading = ref(false)
const currentPage = ref(1)
const itemsPerPage = ref(10)
const searchText = ref('')
const filters = ref({})
const orderBy = ref(null)
const actions = ref(getRHDigitalMockActions())
const showDialogConfirmDelet = ref(false)
const itemToDelete = ref(null)

const quickSearchConfig = computed(() => props.entity.pages.list.filters.quickSearch)

async function fetchData(params = {}) {
    loading.value = true

    try {
        currentPage.value = params.page ?? currentPage.value

        if (orderBy.value) {
            params.orderBy = orderBy.value.key
        }

        if (searchText.value) {
            const field = quickSearchConfig.value.searchBy[0]
            params[field] = `%${searchText.value}%`
        }

        if (filters.value && Object.keys(filters.value).length > 0) {
            params = {
                ...filters.value,
                ...params,
            }
        }

        const response = await props.fetcher({
            limit: itemsPerPage.value,
            page: currentPage.value,
            ...params,
        })

        items.value = response.data.items ?? response.data
        totalItems.value = response.page
    } finally {
        loading.value = false
    }
}

function onAdvancedFilter(filtersAdvanced) {
    filters.value = filtersAdvanced
    fetchData(filtersAdvanced)
}

function onCleanFilter() {
    filters.value = {}
    fetchData()
}

function onPageChange(page) {
    fetchData({ page })
}

function onOrderBy(orderByArray) {
    orderBy.value = orderByArray
}

function onNoopAction(item) {
    itemToDelete.value = item
}

function onDelete(item) {
    itemToDelete.value = item
    showDialogConfirmDelet.value = true
}

function confirmDelete() {
    showDialogConfirmDelet.value = false
}

onMounted(fetchData)
</script>

<template>
    <BasePage :entity="entity">
        <v-row class="mb-4">
            <v-col>
                <ButtonNew
                    :route="entity.pages.create.route?.at(-1)"
                    :tooltip-text="$t('common.labels.availableSoon')"
                />
            </v-col>
            <v-col class="base-advanced-filter">
                <BaseAdvancedFilter
                    :entity="entity"
                    @filter="onAdvancedFilter"
                    @clean="onCleanFilter"
                />
            </v-col>
            <v-col class="base-quick-search">
                <BaseQuickSearch
                    :entity="entity"
                    :config="quickSearchConfig"
                    v-model="searchText"
                    :fetchData="fetchData"
                />
            </v-col>
        </v-row>

        <BaseTable
            :fetchData="fetchData"
            :entity="entity"
            :items="items"
            :loading="loading"
            :totalItems="totalItems"
            :currentPage="currentPage"
            :itemsPerPage="itemsPerPage"
            :actions="actions"
            :action-tooltip-text="$t('common.labels.availableSoon')"
            @view="onNoopAction"
            @edit="onNoopAction"
            @delete="onDelete"
            @page-change="onPageChange"
            @onOrderBy="onOrderBy"
        >
            <template #logo="{ row }">
                <RHDigitalLogoCell :src="row.logo" :alt="row.nome" />
            </template>

            <template #status="{ row }">
                <span class="trezo-status-badge" :class="computeClass(row.status)">
                    {{ t('common.labels.' + (row.status ? 'active' : 'inactive')) }}
                </span>
            </template>
        </BaseTable>

        <DialogConfirmDelet
            v-model="showDialogConfirmDelet"
            :item="itemToDelete?.nome"
            @confirm="confirmDelete"
        />
    </BasePage>
</template>
